// frontend/src/pages/Services.js
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiService, getFullImageUrl } from '../services/api';
import SEOHead from '../components/SEOHead';
import { 
  Search, 
  Filter, 
  Grid, 
  List, 
  Settings, 
  Users, 
  Wrench, 
  PhoneCall,
  BookOpen,
  Star,
  Clock,
  DollarSign,
  Eye
} from 'lucide-react';

const Services = () => {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState('grid');

  const category = searchParams.get('category');
  const search = searchParams.get('search');
  const serviceType = searchParams.get('type');

  useEffect(() => {
    fetchServices();
    fetchCategories();
  }, [category, search, serviceType]);

  const fetchServices = async () => {
    try {
      const params = {};
      if (category) params.category = category;
      if (search) params.search = search;
      if (serviceType) params.service_type = serviceType;
      
      const response = await apiService.getServices(params);
      setServices(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiService.getServiceCategories({ parent_only: 'true' });
      setCategories(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const searchValue = formData.get('search');
    
    const params = new URLSearchParams(searchParams);
    if (searchValue) {
      params.set('search', searchValue);
    } else {
      params.delete('search');
    }
    setSearchParams(params);
  };

  const handleCategoryChange = (categorySlug) => {
    const params = new URLSearchParams(searchParams);
    
    if (categorySlug) {
      params.set('category', categorySlug);
    } else {
      params.delete('category');
    }
    
    setSearchParams(params);
  };

  const handleServiceTypeChange = (type) => {
    const params = new URLSearchParams(searchParams);
    
    if (type) {
      params.set('type', type);
    } else {
      params.delete('type');
    }
    
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchParams({});
  };

  const getServiceTypeIcon = (type) => {
    const icons = {
      'consulting': Users,
      'implementation': Settings,
      'support': PhoneCall,
      'training': BookOpen,
      'maintenance': Wrench,
    };
    return icons[type] || Settings;
  };

  const getServiceTypeLabel = (type) => {
    const labels = {
      'consulting': 'مشاوره',
      'implementation': 'اجرا',
      'support': 'پشتیبانی',
      'training': 'آموزش',
      'maintenance': 'نگهداری',
    };
    return labels[type] || type;
  };

  const serviceTypeOptions = [
    { value: '', label: 'همه انواع' },
    { value: 'consulting', label: 'مشاوره' },
    { value: 'implementation', label: 'اجرا' },
    { value: 'support', label: 'پشتیبانی' },
    { value: 'training', label: 'آموزش' },
    { value: 'maintenance', label: 'نگهداری' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  return (
    <>
      <SEOHead 
        title="خدمات تخصصی - تکنو پلیمر اسپادانا"
        description=" گروه صنعتی تکنو پلیمر اسپادانا طراحی و ساخت انواع قالب های لاستیک و تولید انواع قطعات لاستیکی از جمله اورینگ، پکینگ، قطعات صنعتی و خودرویی با انواع متریال (EPDM, HNBR, VITON, SILICON, SBR, NBR)"
        keywords="طراحی و ساخت انواع قالب، تولید قطعات پلاستیکی ،قالب سازی، لاستیک سازی، اورینگ، سیلیکون، پکینگ، دیافراگم، واشر"
        image={getFullImageUrl('/static/images/og-services.jpg')}
      />
      
      <div className="min-h-screen bg-black py-8">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-white mb-4">🛠️ خدمات تخصصی ما</h1>
            <p className="text-gray-400 text-lg">خدمات حرفه‌ای برای موفقیت کسب‌وکار شما</p>
          </div>

          {/* Filters and Search */}
          <div className="bg-gray-900 rounded-2xl p-6 mb-8 border border-gray-800">
            <div className="flex flex-col lg:flex-row gap-6 items-center justify-between">
              {/* Search */}
              <form onSubmit={handleSearch} className="flex-1 w-full lg:w-auto">
                <div className="relative">
                  <input
                    type="text"
                    name="search"
                    placeholder="جستجو در خدمات..."
                    defaultValue={search}
                    className="w-full lg:w-96 pl-12 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                  />
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                </div>
              </form>

              {/* View Mode Toggle */}
              <div className="flex items-center space-x-4 space-x-reverse">
                <select
                  value={serviceType || ''}
                  onChange={(e) => handleServiceTypeChange(e.target.value)}
                  className="bg-gray-800 border border-gray-700 text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                >
                  {serviceTypeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>

                <div className="flex items-center space-x-2 space-x-reverse bg-gray-800 rounded-xl p-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-lg transition-colors ${
                      viewMode === 'grid' ? 'bg-gray-700 text-yellow-400' : 'text-gray-400'
                    }`}
                  >
                    <Grid size={20} />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-lg transition-colors ${
                      viewMode === 'list' ? 'bg-gray-700 text-yellow-400' : 'text-gray-400'
                    }`}
                  >
                    <List size={20} />
                  </button>
                </div>
              </div>
            </div>

            {/* Categories Filter */}
            <div className="mt-6">
              <div className="flex items-center space-x-2 space-x-reverse mb-4">
                <Filter size={20} className="text-gray-400" />
                <span className="font-medium text-gray-300">دسته‌بندی‌ها:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleCategoryChange(null)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    !category 
                      ? 'bg-yellow-400 text-gray-900 shadow-lg' 
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  همه خدمات
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryChange(cat.slug)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      category === cat.slug
                        ? 'bg-yellow-400 text-gray-900 shadow-lg'
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Services Grid/List */}
          {services.length === 0 ? (
            <div className="text-center py-16">
              <Settings className="text-gray-400 h-16 w-16 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-white mb-2">خدمتی یافت نشد</h3>
              <p className="text-gray-400 mb-6">لطفاً فیلترهای جستجو را تغییر دهید.</p>
              <button
                onClick={clearFilters}
                className="bg-yellow-400 text-gray-900 px-6 py-2 rounded-lg hover:bg-yellow-300 transition-colors"
              >
                نمایش همه خدمات
              </button>
            </div>
          ) : (
            <div className={
              viewMode === 'grid' 
                ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
                : 'space-y-6'
            }>
              {services.map((service) => (
                <ServiceCard 
                  key={service.id} 
                  service={service} 
                  viewMode={viewMode}
                  getServiceTypeIcon={getServiceTypeIcon}
                  getServiceTypeLabel={getServiceTypeLabel}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const ServiceCard = ({ service, viewMode, getServiceTypeIcon, getServiceTypeLabel }) => {
  const TypeIcon = getServiceTypeIcon(service.service_type);

  if (viewMode === 'list') {
    return (
      <div className="bg-gray-900 rounded-2xl overflow-hidden border border-gray-800 hover:border-yellow-400 transition-all group">
        <div className="flex flex-col md:flex-row">
          <div className="md:w-64 flex-shrink-0">
            <img
              src={getFullImageUrl(service.featured_image)}
              alt={service.name}
              className="w-full h-48 md:h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </div>
          <div className="flex-1 p-6">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-yellow-400 transition-colors">
                  {service.name}
                </h3>
                <div className="flex items-center space-x-4 space-x-reverse text-sm text-gray-400">
                  <span className="bg-gray-800 px-3 py-1 rounded-full">
                    {service.category_name}
                  </span>
                  <div className="flex items-center space-x-1 space-x-reverse">
                    <TypeIcon size={16} />
                    <span>{getServiceTypeLabel(service.service_type)}</span>
                  </div>
                </div>
              </div>
              {service.is_featured && (
                <div className="bg-yellow-400 text-gray-900 px-2 py-1 rounded-full text-xs font-bold">
                  ویژه
                </div>
              )}
            </div>
            
            <p className="text-gray-300 mb-4 line-clamp-3">{service.short_description}</p>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4 space-x-reverse text-gray-400 text-sm">
                {service.duration && (
                  <div className="flex items-center space-x-1 space-x-reverse">
                    <Clock size={14} />
                    <span>{service.duration}</span>
                  </div>
                )}
                {service.price_range && (
                  <div className="flex items-center space-x-1 space-x-reverse">
                    <DollarSign size={14} />
                    <span>{service.price_range}</span>
                  </div>
                )}
                <div className="flex items-center space-x-1 space-x-reverse">
                  <Eye size={14} />
                  <span>{service.view_count} بازدید</span>
                </div>
              </div>
              
              <Link
                to={`/services/${service.slug}`}
                className="bg-yellow-400 text-gray-900 px-4 py-2 rounded-lg hover:bg-yellow-300 transition-colors font-medium"
              >
                مشاهده جزئیات
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Grid View
  return (
    <div className="bg-gray-900 rounded-2xl overflow-hidden border border-gray-800 hover:border-yellow-400 transition-all group">
      <div className="relative h-48 overflow-hidden">
        <img
          src={getFullImageUrl(service.featured_image)}
          alt={service.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute top-3 left-3 flex space-x-2 space-x-reverse">
          {service.is_featured && (
            <div className="bg-yellow-400 text-gray-900 px-2 py-1 rounded-full text-xs font-bold">
              ویژه
            </div>
          )}
          <div className="bg-gray-800 text-gray-300 px-2 py-1 rounded-full text-xs font-medium">
            {getServiceTypeLabel(service.service_type)}
          </div>
        </div>
      </div>
      
      <div className="p-5">
        <div className="flex items-center space-x-2 space-x-reverse mb-3">
          <TypeIcon size={18} className="text-yellow-400" />
          <span className="text-yellow-400 text-sm font-medium">
            {service.category_name}
          </span>
        </div>

        <h3 className="font-bold text-lg text-white mb-3 group-hover:text-yellow-400 transition-colors line-clamp-2">
          {service.name}
        </h3>
        
        <p className="text-gray-300 text-sm mb-4 line-clamp-3">
          {service.short_description}
        </p>

        <div className="flex items-center justify-between text-gray-400 text-sm border-t border-gray-800 pt-4">
          <div className="flex items-center space-x-3 space-x-reverse">
            {service.duration && (
              <div className="flex items-center space-x-1 space-x-reverse">
                <Clock size={14} />
                <span>{service.duration}</span>
              </div>
            )}
            <div className="flex items-center space-x-1 space-x-reverse">
              <Eye size={14} />
              <span>{service.view_count}</span>
            </div>
          </div>
          
          <Link
            to={`/services/${service.slug}`}
            className="text-yellow-400 hover:text-yellow-300 font-medium text-sm"
          >
            جزئیات بیشتر
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Services;